import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import GraphToolbar from '@/components/graph/GraphToolbar';
import StateEditor from '@/components/graph/StateEditor';
import TransitionEditor from '@/components/graph/TransitionEditor';
import NodeContextMenu from '@/components/graph/NodeContextMenu';
import DeleteConfirmDialog from '@/components/graph/DeleteConfirmDialog';
import StateNode from '@/components/graph/StateNode';
import EffectEditor from '@/components/graph/EffectEditor';
import CompoundStateEditor from '@/components/graph/CompoundStateEditor';
import LiveExecutionOverlay from '@/components/LiveExecutionOverlay';
import { MachineHistoryManager } from '@/state/MachineHistoryManager';
import { toast } from 'sonner';

const nodeTypes = {
  stateNode: StateNode,
};

/**
 * Interactive Graph Editor - Primary Authoring Instrument
 * 
 * The graph is treated as CODE, not as a diagram.
 * This is the primary construction surface for state machines.
 * 
 * MANDATORY INTERACTIONS (Desktop):
 * - Double-click empty canvas → create state
 * - Drag from state edge → create transition
 * - Right-click node → context menu (edit, delete, mark initial, mark final, add effect)
 * - Click edge → edit transition
 * - Selection = authority (Inspector updates immediately)
 * - Ctrl/Cmd+Z → Undo
 * - Ctrl/Cmd+Shift+Z → Redo
 */
const InteractiveGraphEditor = ({ 
  machine, 
  onNodeSelect,
  onMachineUpdate,
  readOnly = false,
  machineId,
  onTraceUpdate
}) => {
  const [toolMode, setToolMode] = useState('select');
  const [editingState, setEditingState] = useState(null);
  const [editingTransition, setEditingTransition] = useState(null);
  const [editingEffect, setEditingEffect] = useState(null);
  const [editingCompound, setEditingCompound] = useState(null);
  const [editorPosition, setEditorPosition] = useState({ x: 0, y: 0 });
  const [contextMenu, setContextMenu] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [liveActiveState, setLiveActiveState] = useState(null);
  const connectingNodeId = useRef(null);
  const contextMenuTargetNode = useRef(null);
  
  // History manager for undo/redo
  const historyRef = useRef(null);
  const [historyStatus, setHistoryStatus] = useState({
    canUndo: false,
    canRedo: false,
    undoDescription: null,
    redoDescription: null
  });

  // Initialize history manager
  useEffect(() => {
    if (machineId) {
      historyRef.current = new MachineHistoryManager(machineId);
      // Push initial state
      if (machine) {
        historyRef.current.push(machine, 'Initial state');
        setHistoryStatus(historyRef.current.getStatus());
      }
    }
  }, [machineId]);

  // Push changes to history when machine updates
  const pushToHistory = useCallback((description) => {
    if (historyRef.current && machine) {
      historyRef.current.push(machine, description);
      setHistoryStatus(historyRef.current.getStatus());
    }
  }, [machine]);

  // Undo handler
  const handleUndo = useCallback(() => {
    if (!historyRef.current) return;
    
    const result = historyRef.current.undo();
    if (result) {
      onMachineUpdate?.(result.machineIR);
      setHistoryStatus(result.status);
      toast.info(`Undo: ${result.description}`);
    }
  }, [onMachineUpdate]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (!historyRef.current) return;
    
    const result = historyRef.current.redo();
    if (result) {
      onMachineUpdate?.(result.machineIR);
      setHistoryStatus(result.status);
      toast.info(`Redo: ${result.description}`);
    }
  }, [onMachineUpdate]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (readOnly) return;
      
      // Ctrl/Cmd + Z = Undo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl/Cmd + Shift + Z = Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
      // Ctrl/Cmd + Y = Redo (alternative)
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo, readOnly]);

  // Wrapper for machine updates that also records history
  const updateMachineWithHistory = useCallback((updatedMachine, description) => {
    onMachineUpdate?.(updatedMachine);
    // Schedule history push after state update
    setTimeout(() => {
      if (historyRef.current) {
        historyRef.current.push(updatedMachine, description);
        setHistoryStatus(historyRef.current.getStatus());
      }
    }, 0);
  }, [onMachineUpdate]);

  // Convert machine IR to React Flow nodes/edges with layout
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    if (!machine || !machine.states) {
      return { nodes: [], edges: [] };
    }

    // Helper to get capabilities for a state
    const getStateCapabilities = (state) => {
      const caps = new Set();
      
      // From entry effects
      state.entry?.forEach(effectName => {
        const effect = machine.effects?.[effectName];
        effect?.required_capabilities?.forEach(cap => caps.add(cap.name));
      });
      
      // From exit effects
      state.exit?.forEach(effectName => {
        const effect = machine.effects?.[effectName];
        effect?.required_capabilities?.forEach(cap => caps.add(cap.name));
      });
      
      // From transition effects
      state.transitions?.forEach(trans => {
        trans.effects?.forEach(effectName => {
          const effect = machine.effects?.[effectName];
          effect?.required_capabilities?.forEach(cap => caps.add(cap.name));
        });
      });
      
      return Array.from(caps);
    };

    const nodes = [];
    const edges = [];

    // Edge color palette - subtle, distinguishable per source state
    const EDGE_COLORS = [
      '#60a5fa', // blue
      '#a78bfa', // violet  
      '#34d399', // emerald
      '#f97316', // orange
      '#f472b6', // pink
      '#22d3ee', // cyan
      '#facc15', // yellow
      '#fb923c', // amber
    ];
    const stateIds = Object.keys(machine.states);
    const stateColorMap = {};
    stateIds.forEach((sid, i) => {
      stateColorMap[sid] = EDGE_COLORS[i % EDGE_COLORS.length];
    });

    // Track parallel edges between same source-target pair for offset
    const edgePairCount = {};
    const edgePairIndex = {};

    // First pass: count edges per source-target pair
    Object.entries(machine.states).forEach(([stateId, state]) => {
      state.transitions?.forEach((transition) => {
        const pairKey = `${stateId}->${transition.target}`;
        edgePairCount[pairKey] = (edgePairCount[pairKey] || 0) + 1;
      });
    });

    // Create nodes
    Object.entries(machine.states).forEach(([stateId, state]) => {
      const isInitial = stateId === machine.initial;
      const isFinal = state.type === 'final';
      const capabilities = getStateCapabilities(state);
      const isCompound = state.type === 'compound';
      const childStates = isCompound && state.states ? Object.keys(state.states) : [];
      const internalInitial = isCompound ? state.initial : null;

      nodes.push({
        id: stateId,
        type: 'stateNode',
        data: {
          label: stateId,
          isInitial,
          isFinal,
          isCompound,
          childStates,
          internalInitial,
          capabilities,
          entryEffects: state.entry || [],
          exitEffects: state.exit || [],
        },
        position: { x: 0, y: 0 },
      });

      // Create edges with color and offset
      state.transitions?.forEach((transition, idx) => {
        const pairKey = `${stateId}->${transition.target}`;
        if (!edgePairIndex[pairKey]) edgePairIndex[pairKey] = 0;
        const pairIdx = edgePairIndex[pairKey]++;
        const pairTotal = edgePairCount[pairKey];
        const color = stateColorMap[stateId];

        // Offset parallel edges so labels don't overlap
        const offset = pairTotal > 1
          ? (pairIdx - (pairTotal - 1) / 2) * 25
          : 0;

        edges.push({
          id: `${stateId}-${transition.target}-${idx}`,
          source: stateId,
          target: transition.target,
          label: transition.event,
          type: 'smoothstep',
          animated: true,
          pathOptions: { offset },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color,
          },
          style: {
            stroke: color,
            strokeWidth: 1.5,
            opacity: 0.85,
          },
          labelStyle: {
            fill: color,
            fontWeight: 600,
            fontSize: 10,
            letterSpacing: '0.02em',
          },
          labelBgStyle: {
            fill: 'rgba(0, 0, 0, 0.85)',
            rx: 3,
          },
          labelBgPadding: [6, 3],
          data: { transition, sourceState: stateId },
        });
      });
    });

    // Auto-layout
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: 'TB', ranksep: 120, nodesep: 100 });

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: 180, height: 80 });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    nodes.forEach((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      node.position = {
        x: nodeWithPosition.x - 90,
        y: nodeWithPosition.y - 40,
      };
    });

    return { nodes, edges };
  }, [machine]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync nodes and edges when machine changes
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Update nodes when live active state changes
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        data: {
          ...node.data,
          isLiveActive: node.id === liveActiveState,
        },
      }))
    );
  }, [liveActiveState, setNodes]);

  // Node click handler
  const onNodeClick = useCallback(
    (event, node) => {
      onNodeSelect && onNodeSelect(node.id);
      setContextMenu(null); // Close any open context menu
    },
    [onNodeSelect]
  );

  // Node context menu (right-click)
  const onNodeContextMenu = useCallback(
    (event, node) => {
      event.preventDefault();
      if (readOnly) return;
      
      const state = machine?.states?.[node.id];
      contextMenuTargetNode.current = node.id;
      
      setContextMenu({
        nodeId: node.id,
        isInitial: node.id === machine.initial,
        isFinal: state?.type === 'final',
        isCompound: state?.type === 'compound',
        position: { x: event.clientX, y: event.clientY }
      });
    },
    [readOnly, machine]
  );

  // Handle Mark as Final
  const handleMarkFinal = useCallback((stateId) => {
    if (!onMachineUpdate || !machine) return;
    
    const updatedStates = { ...machine.states };
    if (updatedStates[stateId]) {
      updatedStates[stateId] = {
        ...updatedStates[stateId],
        type: 'final'
      };
    }
    
    const updatedMachine = { ...machine, states: updatedStates };
    updateMachineWithHistory(updatedMachine, `Mark ${stateId} as final`);
    toast.success(`${stateId} marked as final`);
  }, [machine, updateMachineWithHistory]);

  // Handle Add Transition from context menu
  const handleAddTransitionFromContext = useCallback(() => {
    const stateId = contextMenuTargetNode.current;
    if (!stateId) return;
    
    // Set up transition editor for this state
    setEditingTransition({
      isNew: true,
      fromState: stateId,
      event: '',
      target: '',
      guards: [],
      effects: []
    });
    setEditorPosition(contextMenu?.position || { x: 200, y: 200 });
  }, [contextMenu]);

  // Handle Add Effect from context menu
  const handleAddEffectFromContext = useCallback(() => {
    setEditingEffect({ isNew: true, stateId: contextMenuTargetNode.current });
    setEditorPosition(contextMenu?.position || { x: 200, y: 200 });
  }, [contextMenu]);

  // Handle Make Compound from context menu
  const handleMakeCompoundFromContext = useCallback(() => {
    const stateId = contextMenuTargetNode.current;
    if (!stateId || !machine?.states?.[stateId]) return;
    
    setEditingCompound({
      isNew: false,
      state: { ...machine.states[stateId], id: stateId }
    });
    setEditorPosition(contextMenu?.position || { x: 200, y: 200 });
  }, [contextMenu, machine]);

  // Handle save effect
  const handleSaveEffect = useCallback((effectData) => {
    if (!onMachineUpdate || !machine) return;
    
    const updatedEffects = { ...machine.effects };
    updatedEffects[effectData.name] = effectData;
    
    const updatedMachine = { ...machine, effects: updatedEffects };
    updateMachineWithHistory(updatedMachine, `Add effect ${effectData.name}`);
    setEditingEffect(null);
    toast.success(`Effect "${effectData.name}" created`);
  }, [machine, updateMachineWithHistory]);

  // Handle save compound state
  const handleSaveCompound = useCallback((compoundData) => {
    if (!onMachineUpdate || !machine) return;
    
    const updatedStates = { ...machine.states };
    updatedStates[compoundData.id] = compoundData;
    
    const updatedMachine = { ...machine, states: updatedStates };
    updateMachineWithHistory(updatedMachine, `Update ${compoundData.id} to compound`);
    setEditingCompound(null);
    toast.success(`"${compoundData.id}" is now a compound state`);
  }, [machine, updateMachineWithHistory]);

  // Handle delete state with constraint checking
  const handleDeleteState = (stateId) => {
    if (!onMachineUpdate) return;

    const constraints = [];
    const state = machine.states[stateId];

    // Check if it's the initial state
    if (stateId === machine.initial) {
      if (Object.keys(machine.states).length === 1) {
        constraints.push('Cannot delete the only state in the machine');
      } else {
        constraints.push('This is the initial state. You must mark another state as initial first');
      }
    }

    // Check for incoming transitions
    const incomingTransitions = [];
    Object.entries(machine.states).forEach(([sid, s]) => {
      s.transitions?.forEach(t => {
        if (t.target === stateId && sid !== stateId) {
          incomingTransitions.push(`Transition from '${sid}' via '${t.event}'`);
        }
      });
    });

    if (incomingTransitions.length > 0) {
      constraints.push(`Remove ${incomingTransitions.length} incoming transition(s):`);
      constraints.push(...incomingTransitions.slice(0, 3));
      if (incomingTransitions.length > 3) {
        constraints.push(`...and ${incomingTransitions.length - 3} more`);
      }
    }

    // Check for outgoing transitions
    if (state.transitions && state.transitions.length > 0) {
      constraints.push(`Remove ${state.transitions.length} outgoing transition(s)`);
    }

    const canDelete = !constraints.some(c => 
      c.includes('Cannot delete') || c.includes('must mark another')
    );

    setDeleteDialog({
      type: 'state',
      itemName: stateId,
      constraints,
      canDelete,
      onConfirm: () => confirmDeleteState(stateId)
    });
  };

  const confirmDeleteState = (stateId) => {
    try {
      const updatedMachine = { ...machine };
      
      // Remove the state
      delete updatedMachine.states[stateId];
      
      // Remove all transitions pointing to this state
      Object.values(updatedMachine.states).forEach(state => {
        if (state.transitions) {
          state.transitions = state.transitions.filter(t => t.target !== stateId);
        }
      });

      onMachineUpdate(updatedMachine);
      setDeleteDialog(null);
      toast.success(`State '${stateId}' deleted`);
    } catch (error) {
      toast.error(`Failed to delete state: ${error.message}`);
    }
  };

  // Handle delete transition
  const handleDeleteTransition = (edgeId, sourceState, targetState, event) => {
    setDeleteDialog({
      type: 'transition',
      itemName: `${sourceState} → ${targetState} (${event})`,
      constraints: [],
      canDelete: true,
      onConfirm: () => confirmDeleteTransition(sourceState, event, targetState)
    });
  };

  const confirmDeleteTransition = (sourceState, event, target) => {
    try {
      const updatedMachine = { ...machine };
      const state = updatedMachine.states[sourceState];
      
      if (state && state.transitions) {
        state.transitions = state.transitions.filter(
          t => !(t.event === event && t.target === target)
        );
      }

      onMachineUpdate(updatedMachine);
      setDeleteDialog(null);
      toast.success('Transition deleted');
    } catch (error) {
      toast.error(`Failed to delete transition: ${error.message}`);
    }
  };

  // Handle mark as initial
  const handleMarkInitial = (stateId) => {
    if (!onMachineUpdate) return;

    try {
      const updatedMachine = { ...machine };
      updatedMachine.initial = stateId;
      
      onMachineUpdate(updatedMachine);
      toast.success(`'${stateId}' marked as initial state`);
    } catch (error) {
      toast.error(`Failed to mark as initial: ${error.message}`);
    }
  };

  // Edge click handler
  const onEdgeClick = useCallback(
    (event, edge) => {
      if (readOnly) return;
      
      // Open transition editor
      setEditingTransition({
        transition: edge.data?.transition,
        sourceState: edge.data?.sourceState,
        targetState: edge.target,
        edgeId: edge.id
      });
      setEditorPosition({ x: event.clientX, y: event.clientY });
    },
    [readOnly]
  );

  // Canvas click handler for adding states
  const onPaneClick = useCallback(
    (event) => {
      if (readOnly || toolMode !== 'addState') return;
      
      // Get click position relative to canvas
      const bounds = event.target.getBoundingClientRect();
      const x = event.clientX - bounds.left;
      const y = event.clientY - bounds.top;
      
      setEditingState({ isNew: true });
      setEditorPosition({ x: event.clientX, y: event.clientY });
      setToolMode('select');
    },
    [readOnly, toolMode]
  );

  // Connection handlers for drag-to-connect
  const onConnect = useCallback((params) => {
    if (readOnly) return;

    // Normalize direction: ensure the connection flows from
    // the node the user started dragging from (source) to the
    // node they dropped onto (target). React Flow may swap
    // source/target when dragging from a target handle to a
    // source handle, so we detect and correct that.
    let source = params.source;
    let target = params.target;

    // If user dragged FROM a target handle, React Flow reports
    // the origin as "target" in the params, reversing the direction.
    // connectingNodeId tracks the real first-clicked node.
    if (connectingNodeId.current && connectingNodeId.current !== source) {
      source = connectingNodeId.current;
      target = params.source === connectingNodeId.current ? params.target : params.source;
    }

    // Open transition editor for the new connection
    setEditingTransition({
      isNew: true,
      sourceState: source,
      targetState: target,
    });
    setEditorPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  }, [readOnly]);

  const onConnectStart = useCallback((event, { nodeId }) => {
    connectingNodeId.current = nodeId;
  }, []);

  const onConnectEnd = useCallback((event) => {
    connectingNodeId.current = null;
  }, []);

  // Tool handlers
  const handleAddState = () => {
    if (readOnly) {
      toast.error('Cannot edit in read-only mode');
      return;
    }
    setToolMode('addState');
    toast.info('Click on canvas to add state');
  };

  const handleAddTransition = () => {
    if (readOnly) {
      toast.error('Cannot edit in read-only mode');
      return;
    }
    setToolMode('addTransition');
    toast.info('Click source state, then target state');
  };

  // Save state handler
  const handleSaveState = (stateData) => {
    if (!onMachineUpdate) {
      toast.error('Machine update handler not configured');
      return;
    }

    try {
      // Create new state in machine IR
      const updatedMachine = { ...machine };
      
      if (editingState?.isNew) {
        // Add new state
        if (updatedMachine.states[stateData.id]) {
          toast.error(`State '${stateData.id}' already exists`);
          return;
        }

        updatedMachine.states[stateData.id] = {
          id: stateData.id,
          type: stateData.type,
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
        };

        if (stateData.markAsInitial) {
          updatedMachine.initial = stateData.id;
        }

        toast.success(`State '${stateData.id}' created`);
      } else {
        // Update existing state
        const existingState = updatedMachine.states[editingState.stateId];
        updatedMachine.states[stateData.id] = {
          ...existingState,
          id: stateData.id,
          type: stateData.type,
        };

        // If renamed, update references
        if (editingState.stateId !== stateData.id) {
          delete updatedMachine.states[editingState.stateId];
          // Update transitions pointing to this state
          Object.values(updatedMachine.states).forEach(state => {
            state.transitions?.forEach(trans => {
              if (trans.target === editingState.stateId) {
                trans.target = stateData.id;
              }
            });
          });
        }

        toast.success(`State '${stateData.id}' updated`);
      }

      onMachineUpdate(updatedMachine);
      setEditingState(null);
    } catch (error) {
      toast.error(`Failed to save state: ${error.message}`);
    }
  };

  // Save transition handler
  const handleSaveTransition = (transitionData) => {
    if (!onMachineUpdate) {
      toast.error('Machine update handler not configured');
      return;
    }

    try {
      const updatedMachine = { ...machine };
      const sourceState = updatedMachine.states[editingTransition.sourceState];

      if (!sourceState) {
        toast.error('Source state not found');
        return;
      }

      if (editingTransition.isNew) {
        // Add new transition (from drag connection or add button)
        if (!sourceState.transitions) {
          sourceState.transitions = [];
        }
        sourceState.transitions.push(transitionData);
        toast.success('Transition created');
      } else if (editingTransition.transition) {
        // Update existing transition
        const transIdx = sourceState.transitions.findIndex(
          t => t.event === editingTransition.transition.event && 
               t.target === editingTransition.transition.target
        );
        if (transIdx >= 0) {
          sourceState.transitions[transIdx] = transitionData;
        }
        toast.success('Transition updated');
      }

      onMachineUpdate(updatedMachine);
      setEditingTransition(null);
    } catch (error) {
      toast.error(`Failed to save transition: ${error.message}`);
    }
  };

  const availableStates = machine ? Object.keys(machine.states) : [];
  const availableGuards = machine ? Object.keys(machine.guards || {}) : [];
  const availableEffects = machine ? Object.keys(machine.effects || {}) : [];

  // Handle group action (placeholder for compound states)
  const handleGroup = () => {
    toast.info('Compound states coming in next phase');
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-950 to-slate-900 relative" data-testid="interactive-graph-editor">
      {/* Desktop-Grade Persistent Toolbar */}
      {!readOnly && (
        <GraphToolbar
          onAddState={handleAddState}
          onAddTransition={handleAddTransition}
          onMarkInitial={() => toast.info('Select a state first, then right-click to mark as initial')}
          onGroup={handleGroup}
          onZoomIn={() => toast.info('Use scroll wheel or pinch to zoom')}
          onZoomOut={() => toast.info('Use scroll wheel or pinch to zoom')}
          onFitView={() => toast.info('Press 0 or use Controls to fit view')}
          mode={toolMode}
          onModeChange={setToolMode}
          canUndo={historyStatus.canUndo}
          canRedo={historyStatus.canRedo}
          onUndo={handleUndo}
          onRedo={handleRedo}
          undoDescription={historyStatus.undoDescription}
          redoDescription={historyStatus.redoDescription}
        />
      )}

      {/* Live Execution Overlay - Collapse Design/Run Boundary */}
      <div className="absolute top-16 right-4 z-20">
        <LiveExecutionOverlay
          machineId={machineId}
          machine={machine}
          activeState={liveActiveState}
          onStateChange={setLiveActiveState}
          onTraceUpdate={onTraceUpdate}
        />
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeContextMenu={onNodeContextMenu}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        fitView
        attributionPosition="bottom-left"
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={true}
        nodeTypes={nodeTypes}
      >
        <Background color="#374151" gap={16} />
        <Controls className="bg-black/50 border border-white/10" />
        <MiniMap
          className="bg-black/50 border border-white/10"
          nodeColor="#6366f1"
        />
      </ReactFlow>

      {/* Context Menu */}
      {contextMenu && (
        <NodeContextMenu
          nodeId={contextMenu.nodeId}
          isInitial={contextMenu.isInitial}
          isFinal={contextMenu.isFinal}
          isCompound={contextMenu.isCompound}
          position={contextMenu.position}
          onEdit={() => {
            setEditingState({ 
              stateId: contextMenu.nodeId,
              isNew: false 
            });
            setEditorPosition(contextMenu.position);
          }}
          onDelete={() => handleDeleteState(contextMenu.nodeId)}
          onMarkInitial={() => handleMarkInitial(contextMenu.nodeId)}
          onMarkFinal={() => handleMarkFinal(contextMenu.nodeId)}
          onAddTransition={handleAddTransitionFromContext}
          onAddEffect={handleAddEffectFromContext}
          onMakeCompound={handleMakeCompoundFromContext}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialog && (
        <DeleteConfirmDialog
          open={true}
          type={deleteDialog.type}
          itemName={deleteDialog.itemName}
          constraints={deleteDialog.constraints}
          canDelete={deleteDialog.canDelete}
          onConfirm={deleteDialog.onConfirm}
          onCancel={() => setDeleteDialog(null)}
        />
      )}

      {/* State Editor - Inline desktop style */}
      {editingState && (
        <div 
          style={{ 
            position: 'fixed',
            left: Math.min(editorPosition.x, window.innerWidth - 320),
            top: Math.min(editorPosition.y, window.innerHeight - 400)
          }}
          className="z-50"
        >
          <StateEditor
            state={editingState.isNew ? null : machine.states[editingState.stateId]}
            isInitial={editingState.stateId === machine.initial}
            onSave={handleSaveState}
            onCancel={() => setEditingState(null)}
            inline={true}
          />
        </div>
      )}

      {/* Transition Editor - Inline desktop style */}
      {editingTransition && (
        <div 
          style={{ 
            position: 'fixed',
            left: Math.min(editorPosition.x, window.innerWidth - 360),
            top: Math.min(editorPosition.y, window.innerHeight - 450)
          }}
          className="z-50"
        >
          <TransitionEditor
            transition={editingTransition.transition}
            sourceState={editingTransition.sourceState}
            targetState={editingTransition.targetState}
            availableStates={availableStates}
            availableGuards={availableGuards}
            availableEffects={availableEffects}
            onSave={handleSaveTransition}
            onCancel={() => setEditingTransition(null)}
            inline={true}
          />
        </div>
      )}

      {/* Effect Editor - Inline desktop style */}
      {editingEffect && (
        <div 
          style={{ 
            position: 'fixed',
            left: Math.min(editorPosition.x, window.innerWidth - 340),
            top: Math.min(editorPosition.y, window.innerHeight - 450)
          }}
          className="z-50"
        >
          <EffectEditor
            effect={editingEffect.effect}
            existingEffects={Object.keys(machine?.effects || {})}
            onSave={handleSaveEffect}
            onCancel={() => setEditingEffect(null)}
            inline={true}
          />
        </div>
      )}

      {/* Compound State Editor - Inline desktop style */}
      {editingCompound && (
        <div 
          style={{ 
            position: 'fixed',
            left: Math.min(editorPosition.x, window.innerWidth - 340),
            top: Math.min(editorPosition.y, window.innerHeight - 450)
          }}
          className="z-50"
        >
          <CompoundStateEditor
            state={editingCompound.state}
            existingStates={Object.keys(machine?.states || {})}
            machine={machine}
            onSave={handleSaveCompound}
            onCancel={() => setEditingCompound(null)}
            inline={true}
          />
        </div>
      )}
    </div>
  );
};

export default InteractiveGraphEditor;
